// src/pages/Summary.jsx
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useEffect, useState } from "react";
import { ShoppingBag, Package, Clock } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// API Helper – token + auto-refresh (plain JS)
// ─────────────────────────────────────────────────────────────────────────────
const apiFetch = async (url, options = {}) => {
  let token = localStorage.getItem("accessToken");

  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

 if (res.status === 401) {
  const refresh = localStorage.getItem("refreshToken");
  if (!refresh) {
    localStorage.clear();
    window.location.href = "/login";
    return;
  }

  const refreshRes = await fetch("/api/token/refresh/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  });

  if (refreshRes.ok) {
    const { access } = await refreshRes.json();
    localStorage.setItem("accessToken", access);
    return apiFetch(url, options);
  } else {
    console.error("Refresh failed:", await refreshRes.json());
    localStorage.clear();
    window.location.href = "/login";
  }
}
  return res;
};

// ─────────────────────────────────────────────────────────────────────────────
export default function Summary() {
  const location = useLocation();
  const navigate = useNavigate();

  const address = location.state?.address;
  const cartItems = location.state?.cartItems || [];
  const total = location.state?.total || 0;

  const isFirstOrder = !localStorage.getItem("hasOrderedBefore");

  const [tiers, setTiers] = useState({
    first_time_percent: 0,
    gt_100_percent: 0,
    gt_500_percent: 0,
    gt_1000_percent: 0,
    gt_2000_percent: 0,
  });

  const [discountApplied, setDiscountApplied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [offerLoading, setOfferLoading] = useState(true);

  // ────────────────────── FETCH OFFERS ──────────────────────
  useEffect(() => {
    apiFetch("/offer/get/")
      .then((r) => r.json())
      .then((data) => {
        setTiers(data);
        setOfferLoading(false);
      })
      .catch(() => {
        console.warn("Offer API down – using defaults");
        setTiers({
          first_time_percent: 10,
          gt_100_percent: 5,
          gt_500_percent: 10,
          gt_1000_percent: 15,
          gt_2000_percent: 20,
        });
        setOfferLoading(false);
      });
  }, []);

  // ────────────────────── VALIDATE STATE ──────────────────────
  useEffect(() => {
    if (!address || cartItems.length === 0) {
      navigate("/cart");
    }
  }, [address, cartItems, navigate]);

  // ────────────────────── CALCULATE DISCOUNT ──────────────────────
  const calculateDiscount = () => {
    let percent = 0;
    let tierName = "";

    if (isFirstOrder && tiers.first_time_percent > 0) {
      percent = Number(tiers.first_time_percent);
      tierName = "First-time";
    } else {
      if (total > 2000 && tiers.gt_2000_percent > percent) {
        percent = Number(tiers.gt_2000_percent);
        tierName = "> ₹2,000";
      }
      if (total > 1000 && tiers.gt_1000_percent > percent) {
        percent = Number(tiers.gt_1000_percent);
        tierName = "> ₹1,000";
      }
      if (total > 500 && tiers.gt_500_percent > percent) {
        percent = Number(tiers.gt_500_percent);
        tierName = "> ₹500";
      }
      if (total > 100 && tiers.gt_100_percent > percent) {
        percent = Number(tiers.gt_100_percent);
        tierName = "> ₹100";
      }
    }

    const amount = (total * percent) / 100;
    return { percent, amount, tierName };
  };

  const { percent: appliedPercent, amount: discountAmount, tierName } =
    discountApplied
      ? calculateDiscount()
      : { percent: 0, amount: 0, tierName: "" };

  const deliveryFee = total > 999 ? 0 : 49;
  const grandTotal = total - discountAmount + deliveryFee;

  // ────────────────────── PLACE ORDER (COD) ──────────────────────
  const handleCOD = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      alert("Please login to place an order.");
      navigate("/login");
      return;
    }

    setLoading(true);
const orderPayload = {
  total: total,
  grand_total: grandTotal,
  discount_amount: discountAmount,
  delivery_fee: deliveryFee,
  address: address,
  cartItems: cartItems.map((item) => ({
    id: item.id,
    quantity: item.quantity,
    price: item.price,
    size: item.size,
    color: item.color,
  })),
};

    try {
      const res = await apiFetch("/api/orders/create/", {
        method: "POST",
        body: JSON.stringify(orderPayload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || err.message || "Order failed");
      }

      const orderData = await res.json();

      localStorage.setItem("hasOrderedBefore", "true");
      localStorage.setItem("cart", JSON.stringify([]));

      navigate("/invoice", {
        state: {
          ...orderData,
          paymentMode: "Cash on Delivery",
          discountAmount,
          appliedPercent,
          tierName,
          estimated_delivery: "5-6 business days",
        },
      });
    } catch (err) {
      console.error("Order Error:", err);
      alert(err.message || "Failed to place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRazorpay = () => {
    alert("Razorpay integration coming soon!");
  };

  // ────────────────────── UI ──────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 py-16">
        <h1 className="text-4xl font-bold text-center text-gray-900 mb-10 flex items-center justify-center gap-3">
          <Package className="w-10 h-10 text-orange-600" />
          Order Summary
        </h1>

        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-2xl border border-gray-200">
          {/* ADDRESS */}
          <div className="mb-12 bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-2xl border border-blue-200">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <ShoppingBag className="w-6 h-6 text-blue-600" />
              Delivery Address
            </h2>
            <div className="space-y-3 text-lg text-gray-700">
              <p>
                <strong>{address?.name}</strong>
                <span className="ml-2 text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                  {address?.type || "House"}
                </span>
              </p>
              <p>
                {address?.house && `${address.house}, `}
                {address?.road}
              </p>
              {address?.nearby && (
                <p className="text-gray-600 italic">
                  Near: <strong>{address.nearby}</strong>
                </p>
              )}
              <p>
                {address?.city}, {address?.state} - {address?.pincode}
              </p>
              <p>
                <strong>Phone:</strong> {address?.phone}
              </p>
              {(address?.weekend_saturday || address?.weekend_sunday) && (
                <p className="text-sm text-blue-700 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Weekend Delivery:{' '}
                  {address?.weekend_saturday && 'Sat'}{' '}
                  {address?.weekend_saturday && address?.weekend_sunday && '+ '}
                  {address?.weekend_sunday && 'Sun'}
                </p>
              )}
              {address?.instructions && (
                <div className="mt-4 p-4 bg-blue-100 border border-blue-300 rounded-lg">
                  <p className="font-medium text-blue-900">Delivery Instructions:</p>
                  <p className="text-blue-800">{address.instructions}</p>
                </div>
              )}
            </div>
          </div>

          {/* CART ITEMS */}
          <div className="space-y-6 mb-12">
            <h2 className="text-xl font-bold text-gray-800">Items ({cartItems.length})</h2>
            {cartItems.map((item, i) => (
              <div key={i} className="flex justify-between items-center bg-gray-50 p-6 rounded-2xl border">
                <div className="flex gap-6">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-24 h-24 rounded-xl object-cover shadow"
                    onError={(e) => {
                      e.currentTarget.src = "https://placehold.co/96x96/cccccc/999999?text=No+Image";
                    }}
                  />
                  <div>
                    <p className="font-bold text-lg">{item.name}</p>
                    <p className="text-gray-600">
                      Size: <strong>{item.size}</strong> | Color: <strong>{item.color}</strong>
                    </p>
                    <p className="text-gray-600">₹{item.price} × {item.quantity}</p>
                  </div>
                </div>
                <p className="font-bold text-lg">₹{(item.price * item.quantity).toFixed(2)}</p>
              </div>
            ))}
          </div>

          {/* OFFER */}
          {offerLoading ? (
            <div className="bg-gray-100 p-8 rounded-2xl text-center">
              <div className="animate-pulse">Loading offers...</div>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-8 rounded-2xl border-2 border-purple-300 mb-12">
              <h2 className="text-2xl font-bold text-purple-900 mb-3">
                Special Offer Active!
              </h2>
              <p className="text-lg text-purple-800">
                {isFirstOrder && tiers.first_time_percent > 0 ? (
                  <>Get <strong>{tiers.first_time_percent}% OFF</strong> on your first order!</>
                ) : (
                  <>Best offer: up to <strong>{Math.max(
                    tiers.gt_100_percent,
                    tiers.gt_500_percent,
                    tiers.gt_1000_percent,
                    tiers.gt_2000_percent
                  )}% OFF</strong>!</>
                )}
              </p>
              <button
                onClick={() => setDiscountApplied(true)}
                disabled={discountApplied}
                className={`mt-5 px-10 py-4 rounded-xl font-bold text-white transition transform hover:scale-105 ${
                  discountApplied ? "bg-green-600" : "bg-purple-600 hover:bg-purple-700"
                }`}
              >
                {discountApplied ? "Offer Applied!" : "Apply Best Offer"}
              </button>
            </div>
          )}

          {/* PRICE */}
          <div className="bg-gray-50 p-8 rounded-2xl mb-12">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Price Details</h2>
            <div className="space-y-4 text-lg">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>₹{total.toFixed(2)}</span>
              </div>
              {discountApplied && discountAmount > 0 && (
                <div className="flex justify-between text-green-600 font-bold">
                  <span>
                    Discount {tierName && `(${tierName})`} ({appliedPercent}%)
                  </span>
                  <span>-₹{discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Delivery</span>
                <span className={deliveryFee === 0 ? "text-green-600" : ""}>
                  {deliveryFee === 0 ? "FREE" : `₹${deliveryFee}`}
                </span>
              </div>
              <hr className="border-gray-300" />
              <div className="flex justify-between text-2xl font-bold text-purple-900">
                <span>Total</span>
                <span>₹{grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* PAYMENT BUTTONS */}
          <div className="grid md:grid-cols-2 gap-6">
            <button
              onClick={handleCOD}
              disabled={loading}
              className={`p-8 rounded-2xl font-bold text-xl transition shadow-lg flex items-center justify-center gap-3 ${
                loading
                  ? "bg-gray-400 cursor-not-allowed text-gray-700"
                  : "bg-orange-600 hover:bg-orange-700 text-white"
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <Package className="w-6 h-6" />
                  Cash on Delivery
                </>
              )}
            </button>

            <button
              onClick={handleRazorpay}
              className="p-8 bg-green-600 text-white rounded-2xl font-bold text-xl hover:bg-green-700 transition shadow-lg flex items-center justify-center gap-3"
            >
              Pay with Razorpay
            </button>
          </div>

          <div className="mt-8 text-center text-sm text-gray-600">
            <p className="flex items-center justify-center gap-2">
              <Clock className="w-5 h-5 text-orange-600" />
              Estimated delivery: <strong>5-6 business days</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}